import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  useCreateInvoice,
  type CreateInvoiceInput,
  type CreateInvoiceResult,
  type InvoiceDeliveryType,
  type InvoiceType,
} from "@/hooks/use-invoice-mutations";
import type { TMTimesheetRow } from "@/hooks/use-tm-invoice-data";
import type { CustomerInfo } from "@/hooks/use-project-detail";
import type { DBInvoice, DBProject } from "@/lib/db-types";
import { useInvoiceCustomer } from "@/hooks/use-invoice-customer";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import { validateInterimAmount } from "@/lib/invoice-plan";
import {
  hasBlockingIssue,
  invoiceFieldIssues,
  resolveInvoiceDefaults,
  type InvoiceFieldValues,
} from "@/lib/invoice-send";
import { InvoiceLinesEditor, type InvoiceLine } from "./InvoiceLinesEditor";
import { InvoiceReferenceFields } from "./InvoiceReferenceFields";
import { InvoiceSendFailedAlert } from "./InvoiceSendFailedAlert";
import { InvoiceCustomerPicker } from "./InvoiceCustomerPicker";
import { InvoiceCustomerNotice } from "./InvoiceCustomerNotice";

/** VIB-1186: T&M-kontekst når dialogen åpnes fra T&M-flyten. */
export interface TMInvoiceContext {
  timeEntryIds: string[];
  vouchers: { id: string; markupPct: number }[];
  timesheet?: TMTimesheetRow[];
  /** VIB-994: satt når fakturaen kommer fra et T&M-mersalg — kobles til
   *  `invoices.upsell_id` etter opprettelse (use-invoice-mutations.ts). */
  upsellId?: string;
}

/** Forhåndsutfylling når opprett-faktura-dialogen åpnes fra Faktura-fanen. */
export interface InvoicePrefill {
  amount?: number;
  description?: string;
  type?: string;
  related_invoice_id?: string;
  /** BUG-M2: settes når fakturaen er en mellomfaktura (posisjon blant mellomleddene). */
  milestone_index?: number | null;
  /** VIB-1186: T&M — forhåndsutfylte linjer + kontekst for fellesdialogen. */
  lines?: { description: string; quantity: number; unit_price: number }[];
  /** Mersalg-IDer som tas med som linjer (sluttfaktura): markeres fakturert etterpå. */
  upsellIds?: string[];
  tmContext?: TMInvoiceContext;
  /** VIB-2836: ad hoc-mellomfaktura som legges inn i planen etter opprettelse. */
  insertIntoPlan?: boolean;
  /** VIB-2836: øvre grense for mellomfakturaens beløp (det som gjenstår). */
  maxAmount?: number;
}

const INVOICE_TYPE_LABELS: Record<string, string> = {
  startup: "Oppstartsfaktura",
  interim: "Mellomfaktura",
  final: "Sluttfaktura",
  upsell: "Tilleggsfaktura",
  credit_note: "Kreditnota",
};

const INVOICE_TYPE_DEFAULT_TEXT: Record<string, string> = {
  startup: "Oppstartshonorar i henhold til avtale.",
  interim: "Delfakturering i henhold til avtale.",
  final: "Sluttoppgjør i henhold til avtale.",
  upsell: "Tilleggsleveranse i henhold til avtale.",
  credit_note: "Kreditering i henhold til avtale.",
};

interface CreateInvoiceFormProps {
  project: DBProject;
  /** Kundekortet: fallback for referanser og faktura-e-post når prosjektet er tomt. */
  customer: CustomerInfo | null;
  onClose: () => void;
  prefill?: InvoicePrefill | null;
  /** VIB-3030: prosjektets fakturaer, for kunder brukt før og kreditnotaens kunde. */
  projectInvoices?: DBInvoice[];
}

/**
 * Opprett faktura / kreditnota mot PowerOffice. Referansefeltene hentes fra
 * prosjektkortet med kundekortet som fallback, og mangler markeres i dialogen
 * før noe sendes. Nekter PO å sende, holdes dialogen åpen med forklaring.
 */
export default function CreateInvoiceForm({ project, customer, onClose, prefill, projectInvoices = [] }: CreateInvoiceFormProps) {
  const prefillType = prefill?.type;
  const isTM = !!prefill?.tmContext;
  const [invoiceType, setInvoiceType] = useState<string>(prefillType ?? "startup");
  // Kreditnota utledes av valgt type, ikke bare av forhåndsvalget: velger
  // brukeren «Kreditnota» i nedtrekksmenyen skal beløpet vises negativt.
  const isCreditNote = invoiceType === "credit_note";
  const [description, setDescription] = useState(
    prefill?.description ?? `${INVOICE_TYPE_LABELS[prefillType ?? "startup"] ?? "Faktura"} ${project.name}`,
  );
  // Hovedkunden: prosjektets kunde, ellers kundekortet prosjektkortet sendte inn.
  const mainCustomerId = project.customer_id ?? customer?.id ?? null;
  const billTo = useInvoiceCustomer({
    mainCustomerId,
    mainCustomer: customer,
    projectInvoices,
    isCreditNote,
    relatedInvoiceId: prefill?.related_invoice_id,
  });
  const defaults = resolveInvoiceDefaults(project, billTo.customer, billTo.isMainCustomer);
  const fieldsFrom = (d: typeof defaults): InvoiceFieldValues => ({
    customerReference: d.customerReference.value ?? "",
    purchaseOrderReference: d.purchaseOrderReference.value ?? "",
    paymentDueDays: d.paymentDueDays.value ?? "",
    deliveryEmail: d.deliveryEmail.value ?? "",
  });
  const [fields, setFields] = useState<InvoiceFieldValues>(() => fieldsFrom(defaults));
  // VIB-3030: ny kunde gir nye referanser. Prosjektets felt er hovedkundens.
  const changeCustomer = (customerId: string) => {
    billTo.select(customerId);
    const next = billTo.customers.find((c) => c.id === customerId) ?? null;
    setFields(fieldsFrom(resolveInvoiceDefaults(project, next, customerId === mainCustomerId)));
  };
  const [deliveryType, setDeliveryType] = useState<InvoiceDeliveryType>("Auto");
  // VIB-1186: T&M-faktura starter som utkast (Erik sender manuelt etterpå).
  const [sendNow, setSendNow] = useState(!isTM);
  const [sendFailure, setSendFailure] = useState<CreateInvoiceResult | null>(null);
  const [lines, setLines] = useState<InvoiceLine[]>(() => {
    if (prefill?.lines && prefill.lines.length > 0) {
      return prefill.lines.map((l) => ({
        id: crypto.randomUUID(),
        description: l.description,
        quantity: l.quantity,
        unit_price: l.unit_price,
        line_type: "Normal" as const,
      }));
    }
    return [
      {
        id: crypto.randomUUID(),
        description:
          prefill?.description ??
          INVOICE_TYPE_DEFAULT_TEXT[prefillType ?? "startup"] ??
          `Faktura for ${project.name}`,
        quantity: 1,
        unit_price: Math.abs(prefill?.amount ?? 0),
        line_type: "Normal",
      },
    ];
  });
  const createMut = useCreateInvoice();
  // TOPP-6 / BUG-H2: stable idempotency key generated once per dialog session.
  // useRef keeps it constant across re-renders and retries; a fresh dialog mount
  // (new invoice action) gets a fresh key, separating deliberate re-invoicing
  // from accidental double-clicks.
  const idempotencyKey = useRef(crypto.randomUUID());

  // Kreditnota: summen er alltid negativ, uansett fortegn på det som skrives inn
  // (samme regel som `buildOrderLines` i create-invoice).
  const normalLines = lines.filter((l) => l.line_type === "Normal");
  const totalAmount = isCreditNote
    ? -normalLines.reduce((s, l) => s + Math.abs(l.quantity * l.unit_price), 0)
    : normalLines.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  // VIB-2836: en mellomfaktura kan aldri overstige det som gjenstår.
  const amountError =
    prefill?.maxAmount != null && invoiceType === "interim" && totalAmount !== 0
      ? validateInterimAmount(totalAmount, prefill.maxAmount)
      : null;
  const issues = invoiceFieldIssues(fields, { sendNow, deliveryType, isMainCustomer: billTo.isMainCustomer });
  const blocked = (sendNow && hasBlockingIssue(issues)) || (!billTo.isMainCustomer && billTo.notInPowerOffice);

  const updateLine = (id: string, field: keyof InvoiceLine, value: string | number) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };
  const addLine = () =>
    setLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), description: "", quantity: 1, unit_price: 0, line_type: "Normal" },
    ]);
  const addTextLine = () =>
    setLines((prev) => [
      ...prev,
      { id: crypto.randomUUID(), description: "", quantity: 0, unit_price: 0, line_type: "Text" },
    ]);
  const removeLine = (id: string) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  const handleTypeChange = (newType: string) => {
    setInvoiceType(newType);
    // Auto-oppdater fritekst hvis brukeren ikke har endret den manuelt
    const currentDefault = INVOICE_TYPE_DEFAULT_TEXT[invoiceType] ?? "";
    if (lines.length === 1 && lines[0].description === currentDefault) {
      const nextDefault = INVOICE_TYPE_DEFAULT_TEXT[newType] ?? "";
      updateLine(lines[0].id, "description", nextDefault);
    }
    // Default-beskrivelse på header
    const oldDefault = `${INVOICE_TYPE_LABELS[invoiceType] ?? "Faktura"} ${project.name}`;
    if (description === oldDefault) {
      setDescription(`${INVOICE_TYPE_LABELS[newType] ?? "Faktura"} ${project.name}`);
    }
  };

  const handleCreate = async () => {
    const validLines = lines.filter(
      (l) => l.description && (l.line_type === "Text" || l.unit_price !== 0 || l.quantity !== 0),
    );
    const hasNormal = validLines.some((l) => l.line_type === "Normal");
    if (!hasNormal || amountError || blocked) return;

    const input: CreateInvoiceInput = {
      project_id: project.id,
      invoice_type: invoiceType as InvoiceType | "credit_note",
      idempotency_key: idempotencyKey.current,
      lines: validLines.map((l) => ({
        description: l.description,
        // Kreditnota: |pris| og |antall|, edge setter antall negativt.
        quantity: isCreditNote ? Math.abs(l.quantity) : l.quantity,
        unit_price: isCreditNote ? Math.abs(l.unit_price) : l.unit_price,
        line_type: l.line_type,
      })),
      description,
      send: sendNow,
      delivery_type: deliveryType,
    };
    if (fields.customerReference) input.customer_reference = fields.customerReference;
    if (fields.purchaseOrderReference) input.purchase_order_reference = fields.purchaseOrderReference;
    if (fields.paymentDueDays !== "" && fields.paymentDueDays >= 0) input.payment_due_days = fields.paymentDueDays;
    if (fields.deliveryEmail) input.delivery_email = fields.deliveryEmail;
    if (prefill?.related_invoice_id) input.related_invoice_id = prefill.related_invoice_id;
    if (billTo.customerId) input.customer_id = billTo.customerId;
    // BUG-M2: bær milepæl-markøren gjennom til create-invoice så fakturaen
    // skilles fra ekte mersalg i milepæl-telleren.
    if (prefill?.milestone_index != null) input.milestone_index = prefill.milestone_index;
    if (prefill?.insertIntoPlan) input.insert_into_plan = true;
    if (prefill?.upsellIds?.length) input.upsell_ids = prefill.upsellIds;
    // VIB-1186: T&M — marker timer/bilag fakturert + send timeliste-PDF.
    const tm = prefill?.tmContext;
    if (tm) {
      input.tm_time_entry_ids = tm.timeEntryIds;
      if (tm.vouchers.length > 0) input.tm_vouchers = tm.vouchers;
      if (tm.timesheet && tm.timesheet.length > 0) input.timesheet = tm.timesheet;
      // VIB-994: T&M-mersalg kobles til fakturaen via invoices.upsell_id (kan
      // ha flere fakturaer — added_to_invoice_id-én-til-én gjelder ikke T&M).
      if (tm.upsellId) input.tm_upsell_id = tm.upsellId;
    }

    try {
      const result = await createMut.mutateAsync(input);
      // Fakturaen finnes i Go, men ble ikke sendt: hold dialogen åpen og forklar.
      if (result.send_failed) {
        setSendFailure(result);
        return;
      }
      onClose();
    } catch {
      // toast handled in hook
    }
  };

  const title = isCreditNote ? "Opprett kreditnota" : "Opprett faktura";
  const submitLabel = sendNow
    ? isCreditNote ? "Opprett og send kreditnota" : "Opprett og send faktura"
    : isCreditNote ? "Opprett kreditnota-utkast" : "Opprett utkast";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {sendFailure ? (
          <div className="space-y-4">
            <InvoiceSendFailedAlert code={sendFailure.send_error_code} detail={sendFailure.send_error_detail} />
            <div className="flex justify-end">
              <Button size="sm" onClick={onClose}>Lukk</Button>
            </div>
          </div>
        ) : (
        <div className="space-y-4">
          <InvoiceCustomerPicker
            value={billTo.customer}
            mainCustomerId={mainCustomerId}
            customers={billTo.customers}
            projectInvoices={projectInvoices}
            onChange={changeCustomer}
            lockedReason={billTo.lockedReason}
          />
          <InvoiceCustomerNotice
            customerName={billTo.customer?.name ?? null}
            customerId={billTo.customerId}
            isMainCustomer={billTo.isMainCustomer}
            notInPowerOffice={billTo.notInPowerOffice}
          />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="invoice-type" className="text-xs text-muted-foreground mb-1 block">Type</label>
              <select
                id="invoice-type"
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={invoiceType}
                onChange={(e) => handleTypeChange(e.target.value)}
                disabled={prefillType === "credit_note" || isTM || !!prefill?.insertIntoPlan}
              >
                <option value="startup">Oppstartsfaktura</option>
                <option value="interim">Mellomfaktura</option>
                <option value="final">Sluttfaktura</option>
                <option value="upsell">Tilleggsfaktura</option>
                <option value="credit_note">Kreditnota</option>
              </select>
            </div>
            <div>
              <label htmlFor="invoice-delivery" className="text-xs text-muted-foreground mb-1 block">Leveringsmåte</label>
              <select
                id="invoice-delivery"
                className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={deliveryType}
                onChange={(e) => setDeliveryType(e.target.value as InvoiceDeliveryType)}
                disabled={!sendNow}
              >
                <option value="Auto">Auto (EHF hvis mulig, ellers epost)</option>
                <option value="EHF">EHF (kun)</option>
                <option value="PdfByEmail">PDF på epost</option>
                <option value="PdfPrintForDownload">PDF for utskrift</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="invoice-description" className="text-xs text-muted-foreground mb-1 block">
              Beskrivelse (vises som første tekstlinje på fakturaen)
            </label>
            <Input id="invoice-description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <InvoiceReferenceFields
            values={fields}
            defaults={defaults}
            issues={issues}
            onChange={(field, value) => setFields((prev) => ({ ...prev, [field]: value }))}
            sendNow={sendNow}
            deliveryType={deliveryType}
          />

          <InvoiceLinesEditor
            lines={lines}
            onUpdate={updateLine}
            onAdd={addLine}
            onAddText={addTextLine}
            onRemove={removeLine}
          />

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2 text-sm">
              <input
                id="send-now-checkbox"
                type="checkbox"
                checked={sendNow}
                onChange={(e) => setSendNow(e.target.checked)}
                className="h-4 w-4"
              />
              <label htmlFor="send-now-checkbox" className="select-none cursor-pointer">
                Send nå (uten ekstra godkjenning)
              </label>
            </div>
            <div className="text-sm">
              <span className="text-muted-foreground">Eks. mva:</span>{" "}
              <span className="font-bold tabular-nums" data-testid="invoice-total">
                {formatNOK(totalAmount)}
              </span>
            </div>
          </div>

          <div className="text-xs text-muted-foreground">
            {isCreditNote && (
              <p className="mt-1 italic">Beløpet krediteres kunden og trekkes fra det fakturerte.</p>
            )}
            {!sendNow && (
              <p className="mt-1 italic">
                Lages som utkast i Go. Du må sende manuelt derfra etterpå.
              </p>
            )}
          </div>

          {amountError && (
            <p className="text-xs text-destructive">
              {amountError} Gjenstår: {formatNOK(prefill?.maxAmount ?? 0)}.
            </p>
          )}

          {createMut.error && (
            <p role="alert" className="text-xs text-destructive">{(createMut.error as Error).message}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={createMut.isPending}>
              Avbryt
            </Button>
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={createMut.isPending || totalAmount === 0 || !!amountError || blocked}
            >
              {createMut.isPending ? <SrIcon name="loader2" className="w-3 h-3 animate-spin mr-1" /> : null}
              {submitLabel}
            </Button>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
